import React, { useState, useEffect, useRef } from 'react'
import { Search, ChevronDown, Check } from 'lucide-react'

interface Option {
  value: string
  label: string
  data?: any
}

interface SearchableSelectProps {
  label?: string
  placeholder?: string
  options: Option[]
  value: string
  onChange: (value: string, option?: Option) => void
  onSearch?: (search: string) => void
  isLoading?: boolean
  className?: string
  disabled?: boolean
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  placeholder = "Pesquisar...",
  options,
  value,
  onChange,
  onSearch,
  isLoading = false,
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Filtrar opções localmente se não houver onSearch
  useEffect(() => {
    if (!onSearch) {
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredOptions(filtered)
    } else {
      setFilteredOptions(options)
    }
    setHighlightedIndex(-1)
  }, [options, searchTerm, onSearch])

  // Pesquisa externa
  useEffect(() => {
    if (onSearch && searchTerm.trim().length >= 2) {
      const timeoutId = setTimeout(() => {
        onSearch(searchTerm.trim())
      }, 500) // Debounce de 500ms

      return () => clearTimeout(timeoutId)
    } else if (onSearch && searchTerm.trim().length < 2) {
      // Limpar resultados se menos de 2 caracteres
      setFilteredOptions([])
    }
  }, [searchTerm, onSearch])

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Navegação por teclado
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true)
        setHighlightedIndex(0)
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setSearchTerm('')
        inputRef.current?.blur()
        break
    }
  }

  // Scroll para o item destacado
  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest'
        })
      }
    }
  }, [highlightedIndex])

  const handleSelectOption = (option: Option) => {
    onChange(option.value, option)
    setIsOpen(false)
    setSearchTerm('')
    inputRef.current?.blur()
  }

  const selectedOption = options.find(opt => opt.value === value)

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        <div
          className={`
            w-full px-3 py-2 border border-gray-300 rounded-md bg-white cursor-pointer
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400'}
            ${isOpen ? 'border-blue-500 ring-1 ring-blue-500' : ''}
            transition-colors duration-200
          `}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              <Search className="h-4 w-4 text-gray-400" />
              {isOpen ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  className="flex-1 outline-none bg-transparent text-sm"
                  autoFocus
                  disabled={disabled}
                />
              ) : (
                <span className={`flex-1 text-sm ${selectedOption ? 'text-gray-900' : 'text-gray-500'}`}>
                  {selectedOption ? selectedOption.label : placeholder}
                </span>
              )}
            </div>
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                <span className="mt-1 block">Pesquisando...</span>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhuma opção disponível'}
              </div>
            ) : (
              <div ref={listRef}>
                {filteredOptions.map((option, index) => (
                  <div
                    key={option.value}
                    className={`
                      px-3 py-2 cursor-pointer text-sm flex items-center justify-between
                      ${index === highlightedIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}
                      ${option.value === value ? 'bg-blue-100 text-blue-700' : ''}
                    `}
                    onClick={() => handleSelectOption(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <span>{option.label}</span>
                    {option.value === value && (
                      <Check className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchableSelect